/**
 * Chat API — unified Message API client.
 *
 * Calls /chat/rooms/* endpoints introduced in Epic 24 (Phase F schema unification).
 * The legacy messages.js file targeting /messages/direct/* is kept as a backup until
 * the DirectMessage migration script (story 24-2) has been verified in production.
 *
 * Story reference: Epic 25 / 25-3c (CRM API file)
 */
import { apiClient } from './client';

// ─── Rooms ────────────────────────────────────────────────────────────────────

/**
 * List chat rooms the authenticated user is a member of, newest activity first.
 * Each room is { roomId, type: 'dm'|'course'|'support', name, avatarUrl,
 * lastMessage: { id, senderId, contentPreview, createdAt } | null, unreadCount }.
 * The endpoint takes no query parameters: filter by `type` and by name on the client.
 *
 * @returns {Promise<{ rooms: Array }>}
 */
export async function getChatRooms() {
  const { data } = await apiClient.get('/chat/rooms');
  return data;
}

/**
 * Get a single room by ID.
 *
 * @param {string} roomId
 * @returns {Promise<object>} Room object with unreadCount
 */
export async function getChatRoom(roomId) {
  const { data } = await apiClient.get(`/chat/rooms/${roomId}`);
  return data;
}

// ─── Messages ─────────────────────────────────────────────────────────────────

/**
 * Fetch one page of a room's messages, NEWEST FIRST. Pass the previous page's
 * `nextCursor` (an opaque string) as `cursor` to get the page of older messages;
 * `nextCursor` is null on the last page.
 *
 * @param {string} roomId
 * @param {{ cursor?: string, limit?: number }} params  limit is 1-100, default 50
 * @returns {Promise<{ messages: Array, nextCursor: string|null }>}
 */
export async function getRoomMessages(roomId, params = {}) {
  const { data } = await apiClient.get(`/chat/rooms/${roomId}/messages`, { params });
  return data;
}

/**
 * Send a message to a room.
 *
 * @param {string} roomId
 * @param {{ content: string }} body
 * @returns {Promise<object>} The created Message row
 */
export async function sendRoomMessage(roomId, body) {
  const { data } = await apiClient.post(`/chat/rooms/${roomId}/messages`, body);
  return data;
}
